import { useEffect, useState, useMemo, useCallback } from "react";
import { Printer, Loader2, ArrowLeft, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/**
 * VendorsPrint — dedicated printable directory page.
 *
 * Route: /proveedores/imprimir
 * Users open this from the "Imprimir listado" button on Proveedores. The page
 * hides the app chrome via a print stylesheet and prints one row per vendor
 * with: NAME / CIF / ADDRESS / PRODUCT TYPES. Address/product_types are user-
 * editable inline (data lives in the `vendor_details` collection).
 */
export default function VendorsPrint() {
  const { lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({ address: "", phone: "", email: "", product_types: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const catMap = useMemo(() => {
    const m = {};
    for (const c of categories || []) m[c.name] = c;
    return m;
  }, [categories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dir, cats] = await Promise.all([
        api.get("/vendors/directory"),
        api.get("/categories"),
      ]);
      setVendors(dir.data?.vendors || []);
      setCategories(cats.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (v) => {
    setEditingKey(v.group_key);
    setDraft({
      address: v.address || "",
      phone: v.phone || "",
      email: v.email || "",
      product_types: v.product_types || "",
      notes: v.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    setSaving(true);
    try {
      const params = new URLSearchParams({ group_key: editingKey });
      await api.put(`/vendors/details?${params.toString()}`, draft);
      toast.success(lang === "es" ? "Datos guardados" : "Details saved");
      setEditingKey(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const renderProductTypes = (v) => {
    if (v.product_types) return v.product_types;
    const inferred = (v.inferred_categories || [])
      .map((raw) => categoryLabel(catMap[raw] || { name: raw }, lang))
      .join(", ");
    return inferred || "—";
  };

  return (
    <div className="max-w-6xl mx-auto p-8 print:p-2" data-testid="vendors-print-page">
      {/* Print-only stylesheet: hide app chrome, buttons; tighten table */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          table { font-size: 10pt; }
          th, td { padding: 4px 6px !important; }
          @page { margin: 12mm; size: A4; }
        }
      `}</style>

      <div className="flex items-center justify-between mb-6 no-print print-page">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.history.back()}
            data-testid="print-back-btn"
          >
            <ArrowLeft size={14} className="mr-1.5" />
            {lang === "es" ? "Volver" : "Back"}
          </Button>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              {lang === "es" ? "Listado de Proveedores" : "Vendor Directory"}
            </h1>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {vendors.length} {lang === "es" ? "proveedores" : "vendors"}
              {" · "}
              {lang === "es" ? "Haz clic en el lápiz para editar dirección/tipo" : "Click the pencil to edit address/type"}
            </p>
          </div>
        </div>
        <Button
          onClick={() => window.print()}
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          data-testid="print-trigger-btn"
        >
          <Printer size={14} className="mr-1.5" />
          {lang === "es" ? "Imprimir" : "Print"}
        </Button>
      </div>

      {/* Print header — visible on paper only */}
      <div className="hidden print:block mb-4 text-center">
        <h1 className="text-2xl font-bold">
          {lang === "es" ? "Listado de Proveedores" : "Vendor Directory"}
        </h1>
        <p className="text-xs text-gray-600 mt-1">
          {new Date().toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
            year: "numeric", month: "long", day: "numeric",
          })}
        </p>
      </div>

      {loading ? (
        <div className="py-24 text-center text-sm text-[var(--text-muted)]">
          <Loader2 className="inline-block animate-spin mr-2" size={16} />
          {lang === "es" ? "Cargando..." : "Loading..."}
        </div>
      ) : vendors.length === 0 ? (
        <div className="py-24 text-center text-sm text-[var(--text-muted)]">
          {lang === "es" ? "No hay proveedores registrados." : "No vendors yet."}
        </div>
      ) : (
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden print:border-black">
          <table className="w-full text-sm print:text-xs">
            <thead className="bg-[var(--surface-hover)] print:bg-gray-100">
              <tr className="text-left text-xs uppercase tracking-wider text-[var(--text-muted)]">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">{lang === "es" ? "Nombre" : "Name"}</th>
                <th className="px-3 py-2">CIF</th>
                <th className="px-3 py-2">{lang === "es" ? "Dirección" : "Address"}</th>
                <th className="px-3 py-2">{lang === "es" ? "Tipo de producto" : "Product type"}</th>
                <th className="px-3 py-2 no-print" />
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, idx) => {
                const isEditing = editingKey === v.group_key;
                return (
                  <tr
                    key={v.group_key}
                    className="border-t border-[var(--border-color)] print:border-black align-top"
                    data-testid={`vendor-print-row-${idx}`}
                  >
                    <td className="px-3 py-2 text-[var(--text-muted)]">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{v.primary_name}</td>
                    <td className="px-3 py-2 font-mono-num">{v.vendor_tax_id || "—"}</td>
                    <td className="px-3 py-2 whitespace-pre-wrap">
                      {isEditing ? (
                        <Textarea
                          value={draft.address}
                          onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                          rows={2}
                          placeholder={lang === "es" ? "Calle, ciudad, CP..." : "Street, city, ZIP..."}
                          data-testid={`vendor-print-address-input-${idx}`}
                          className="text-xs h-16"
                        />
                      ) : (
                        v.address || <span className="text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <Input
                          value={draft.product_types}
                          onChange={(e) => setDraft({ ...draft, product_types: e.target.value })}
                          placeholder={
                            (v.inferred_categories || []).map((c) => catMap[c]?.name_es || c).join(", ")
                            || (lang === "es" ? "Ej: Carnes, Pescados" : "e.g. Meat, Fish")
                          }
                          className="text-xs h-8"
                          data-testid={`vendor-print-types-input-${idx}`}
                        />
                      ) : (
                        renderProductTypes(v)
                      )}
                    </td>
                    <td className="px-3 py-2 no-print">
                      {isEditing ? (
                        <div className="flex gap-1">
                          <button
                            onClick={saveEdit}
                            disabled={saving}
                            className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--status-positive)]"
                            title={lang === "es" ? "Guardar" : "Save"}
                            data-testid={`vendor-print-save-${idx}`}
                          >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
                            title={lang === "es" ? "Cancelar" : "Cancel"}
                            data-testid={`vendor-print-cancel-${idx}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(v)}
                          className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
                          title={lang === "es" ? "Editar" : "Edit"}
                          data-testid={`vendor-print-edit-${idx}`}
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-[var(--text-muted)] mt-4 no-print">
        {lang === "es"
          ? "Consejo: los datos de dirección y tipo se guardan por proveedor y se preservan aunque se editen sus facturas."
          : "Tip: address and type are stored per vendor and preserved even if their invoices are edited."}
      </p>
    </div>
  );
}
